// src/services/api.ts
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { js } from '../config/env';
import type { TenantData, RegisterResponse } from '../types';
import { loadingBar } from '../lib/loadingBar';
import { clearViewCacheForLogout } from '../lib/viewCache';

class ApiService
{
  private _api: AxiosInstance | null = null;
  private _initialized: boolean = false;
  private _isRefreshing: boolean = false;
  private _refreshSubscribers: Array<(token: string) => void> = [];

  // Getter that initializes the instance only when needed
  private get api(): AxiosInstance
  {
    if (!this._api || !this._initialized)
    {
      this.initialize();
    }
    return this._api!;
  }

  // Getter to obtain the current baseURL
  private get baseUrl(): string
  {
    const config = js.settings;
    return String(config.apiAuthURL || 'http://localhost/Corgee.Web/');
  }

  private initialize()
  {
    let baseURL = this.baseUrl;
    if (!baseURL.endsWith('/'))
    {
      baseURL += '/';
    }

    this._api = axios.create({
      baseURL: baseURL + 'api/',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Interceptor to add the token to every request
    this._api.interceptors.request.use(
      config =>
      {
        // Import authStorage dynamically to avoid a circular dependency
        const token =
          localStorage.getItem('C2_access_token') || localStorage.getItem('access_token');
        if (token && config.headers && !config.url?.includes('Authenticate'))
        {
          config.headers.Authorization = `Bearer ${token}`;
        }

        loadingBar.increment();
        return config;
      },
      error =>
      {
        loadingBar.decrement();
        console.error('❌ Request error:', error);
        return Promise.reject(error);
      },
    );

    // Interceptor to handle response errors
    this._api.interceptors.response.use(
      response =>
      {
        loadingBar.decrement();
        return response;
      },
      async error =>
      {
        loadingBar.decrement();
        const originalRequest = error.config as any;

        console.error('❌ Response error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
        });

        // Handle 401 - expired or invalid token
        if (error.response?.status === 401 && !originalRequest._retry)
        {
          originalRequest._retry = true;

          // Do not attempt a refresh for the authentication endpoint: the 401 means invalid credentials, not an expired session
          if (originalRequest.url?.includes('Authenticate'))
          {
            return Promise.reject(error);
          }

          const refreshToken =
            localStorage.getItem('C2_refresh_token') || localStorage.getItem('refresh_token');
          if (!refreshToken)
          {
            return Promise.reject(error);
          }

          // If a token refresh is already in progress, queue the request
          if (this._isRefreshing)
          {
            return new Promise(resolve =>
            {
              this._refreshSubscribers.push((token: string) =>
              {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this._api!.request(originalRequest));
              });
            });
          }

          // Start the token refresh
          this._isRefreshing = true;

          try
          {
            const newToken = await this.refreshAccessToken();
            this.onRefreshed(newToken);
            return this._api!.request(originalRequest);
          }
          catch (refreshError)
          {
            // If the refresh fails, log out
            this.logOut();
            return Promise.reject(refreshError);
          }
          finally
          {
            this._isRefreshing = false;
            this._refreshSubscribers = [];
          }
        }

        return Promise.reject(error);
      },
    );

    this._initialized = true;
  }

  /**
   * Notify all subscribers that the token has been refreshed
   */
  private onRefreshed(token: string): void
  {
    this._refreshSubscribers.forEach(callback => callback(token));
  }

  /**
   * Refresh the access token using the refresh token
   * @returns Promise with the new access token
   */
  private async refreshAccessToken(): Promise<string>
  {
    const refreshToken =
      localStorage.getItem('C2_refresh_token') || localStorage.getItem('refresh_token');
    const tenancyName =
      localStorage.getItem('C2_tenancyName') || localStorage.getItem('tenancyName') || 'Default';

    if (!refreshToken)
    {
      throw new Error('No refresh token available');
    }

    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', refreshToken);
    formData.append('tenancyName', tenancyName);
    formData.append('Client_id', 'CorgeeWeb');

    try
    {
      const response = await fetch(`${this.baseUrl}token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok)
      {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();

      // Store the new tokens using STORAGE_KEYS
      localStorage.setItem('C2_refresh_token', data.refresh_token);
      localStorage.setItem('C2_access_token', data.access_token);

      return data.access_token;
    }
    catch (error)
    {
      console.error('❌ Failed to refresh access token:', error);
      throw error;
    }
  }

  /**
   * Log the user out - clear all tokens and redirect to the login page
   */
  private logOut(): void
  {
    console.warn('🔐 Logging out user...');

    // Purge view cache and block writes from in-flight requests
    clearViewCacheForLogout();

    // Clear all C2_ keys from localStorage
    Object.keys(localStorage).forEach(key =>
    {
      if (key.startsWith('C2_'))
      {
        localStorage.removeItem(key);
      }
    });

    // Clear all C2_ keys from sessionStorage
    Object.keys(sessionStorage).forEach(key =>
    {
      if (key.startsWith('C2_'))
      {
        sessionStorage.removeItem(key);
      }
    });

    // Redirect to the login page (uses the environment base URL)
    const base = import.meta.env.BASE_URL || '/';
    window.location.href = base;
  }

  // ==================== Generic HTTP methods ====================

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  // ==================== Tenant methods ====================

  async getTenant(tenancyName?: string): Promise<TenantData>
  {
    const name = tenancyName || 'Default';
    const data = await this.get<TenantData>(`Tenant/GetTenant?tenancyName=${name}`);

    return data;
  }

  // ==================== Authentication methods ====================

  async Authenticate(usernameOrEmailAddress: string, password: string, tenancyName?: string)
  {
    const data = await this.post('Account/Authenticate', {
      usernameOrEmailAddress,
      password,
      tenancyName: tenancyName || 'Default',
    });

    return data;
  }

  async register(userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    tenantId?: number;
  }): Promise<RegisterResponse>
  {
    const data = await this.post<RegisterResponse>('Account/Register', userData);

    return data;
  }

  async authenticateWithExternalLogin(
    loginProvider: string,
    providerKey: string,
    tenancyName?: string,
  )
  {
    try
    {
      const queryParams = new URLSearchParams({
        loginProvider: loginProvider,
        providerKey: providerKey,
        tenancyName: tenancyName || 'Default',
      }).toString();

      const url = `${this.baseUrl}api/account/ObtainLocalAccessToken?${queryParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok)
      {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to authenticate with external provider');
      }

      const data = await response.json();

      return data;
    }
    catch (error)
    {
      console.error('❌ Error in authenticateWithExternalLogin:', error);
      throw error;
    }
  }

  // ==================== OAuth URLs ====================

  getGoogleAuthUrl(): string
  {
    return `${this.baseUrl}Auth/Google`;
  }

  getOffice365AuthUrl(): string
  {
    return `${this.baseUrl}Auth/Office365`;
  }
}

// Export a single instance of the service
export const apiService = new ApiService();

// Also export the class in case custom instances are needed
export default ApiService;
